// firebase-auth-context.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onIdTokenChanged } from "firebase/auth";
import { app } from "../services/firebase"; // Import your Firebase app initialization here

const AuthContext = createContext({
  user: null,
  idToken: null,
  refreshIdToken: async () => null,
  ready: false,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [ready, setReady] = useState(false);

  const auth = getAuth(app);

  // Refresh the token manually and store it in sessionStorage
  const refreshIdToken = async (force = false) => {
    const lastUpdated = Number(
      sessionStorage.getItem("firebaseIdTokenTimestamp")
    );
    const tokenAgeMinutes = (Date.now() - lastUpdated) / 1000 / 60;

    if (force || tokenAgeMinutes > 50) {
      // Force refresh if older than 50 minutes or if forced
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken(true); // force refresh
        setIdToken(token);
        sessionStorage.setItem("firebaseIdToken", token);
        sessionStorage.setItem("firebaseIdTokenTimestamp", Date.now());
        return token;
      }
      return null;
    }

    // Use cached token from sessionStorage if it's not expired
    return sessionStorage.getItem("firebaseIdToken");
  };

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        const token = await user.getIdToken();
        setIdToken(token);
        sessionStorage.setItem("firebaseIdToken", token);
        sessionStorage.setItem("firebaseIdTokenTimestamp", Date.now());
      } else {
        setIdToken(null);
        sessionStorage.removeItem("firebaseIdToken");
        sessionStorage.removeItem("firebaseIdTokenTimestamp");
      }
      setReady(true);
    });

    return () => unsubscribe();
  }, [auth]);

  return (
    <AuthContext.Provider value={{ user, idToken, refreshIdToken, setIdToken, ready }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use Firebase Auth context
export const useFirebaseAuth = () => {
  return useContext(AuthContext);
};
